import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, User, Mail, Shield, Globe, Building2, Users, StickyNote, Activity, Lock, Trash2, Loader2, UserX, UserCheck, Layers, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";
import { StaffPasswordCard } from "@/components/StaffPasswordCard";
import { HolidayPay } from "@/components/HolidayPay";
import { Palmtree } from "lucide-react";

const OA_LABEL = { CONNECT_ONLY: "Connect only", FIELD_ONLY: "Field only", CONNECT_FIELD: "Connect + Field" };
const OA_STYLE = { CONNECT_ONLY: "bg-sky-50 text-sky-700 border-sky-200", FIELD_ONLY: "bg-violet-50 text-violet-700 border-violet-200", CONNECT_FIELD: "bg-emerald-50 text-emerald-700 border-emerald-200" };
const OA_OPTIONS = [
  { value: "CONNECT_ONLY", label: "Connect only", desc: "Static sites, shifts and clock in / out." },
  { value: "FIELD_ONLY", label: "Field only", desc: "Field jobs and advanced field operations." },
  { value: "CONNECT_FIELD", label: "Connect + Field", desc: "Access to both Connect and Field." },
];
const MANAGER_ROLES = { cleaner: ["supervisor", "admin", "owner"], supervisor: ["admin", "owner"], office: ["supervisor", "admin", "owner"], admin: ["owner"], owner: [] };

const ROLE_BADGE = { owner: "bg-brand/10 text-brand border-brand/20", admin: "bg-purple-50 text-purple-700 border-purple-200", supervisor: "bg-blue-50 text-blue-700 border-blue-200", office: "bg-slate-100 text-slate-600", cleaner: "bg-emerald-50 text-emerald-700 border-emerald-200" };
const roleLabel = (r) => ({ owner: "Owner", admin: "Admin", supervisor: "Supervisor", office: "Office", cleaner: "Cleaner" }[r] || r);

export default function StaffProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const { can, messagingEnabled, user: me } = useAuth();
  const isOA = ["owner", "admin"].includes(me?.role);
  const [u, setU] = useState(null);
  const [access, setAccess] = useState(null);
  const [sites, setSites] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [siteSel, setSiteSel] = useState([]);
  const [supervisorId, setSupervisorId] = useState("");
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [activity, setActivity] = useState({ items: [], jobCount: 0 });
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [oaValue, setOaValue] = useState("CONNECT_ONLY");
  const [savingOA, setSavingOA] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  const loadUser = useCallback(async () => {
    try {
      const { data } = await client.get("/users");
      const found = data.find((x) => x.id === id) || null;
      setU(found);
      setAllUsers(data);
      if (found) setForm({
        displayName: found.displayName || "", email: found.email || "",
        role: found.role || "cleaner", workerType: found.workerType || "static",
        languagePref: found.languagePref || "en", status: found.status || "active",
        reportsTo: found.reportsTo || "", phone: found.phone || "",
      });
      if (found) setOaValue(found.operationalAccess || "CONNECT_ONLY");
    } catch { /* */ }
  }, [id]);

  useEffect(() => {
    loadUser();
    client.get("/sites").then((r) => setSites(r.data)).catch(() => {});
    client.get("/users").then((r) => setSupervisors(r.data.filter((x) => x.role === "supervisor"))).catch(() => {});
    if (can("permission.manage")) client.get(`/users/${id}/access`).then((r) => {
      setAccess(r.data);
      setSiteSel((r.data.siteAssignments || []).map((a) => a.siteId));
      setSupervisorId((r.data.supervisors || [])[0]?.supervisorUserId || "");
    }).catch(() => {});
    if (can("user.manage")) client.get(`/users/${id}/private-notes`).then((r) => setNotes(r.data)).catch(() => {});
    if (can("user.view")) client.get(`/users/${id}/activity`).then((r) => setActivity(r.data)).catch(() => {});
    if (can("user.manage")) client.get("/meta/permissions").then((r) => setMeta(r.data)).catch(() => {});
  }, [id, can, loadUser]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await client.patch(`/users/${id}`, {
        displayName: form.displayName, email: form.email, role: form.role,
        workerType: form.workerType, languagePref: form.languagePref, status: form.status,
        reportsTo: form.reportsTo || "", phone: form.phone || "",
      });
      toast.success(t("common.saved", "Saved"));
      loadUser();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const saveOperationalAccess = async () => {
    setSavingOA(true);
    try {
      await client.patch(`/users/${id}/operational-access`, { operationalAccess: oaValue });
      toast.success(t("staff.oaUpdated", "Operational access updated"));
      loadUser();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSavingOA(false); }
  };

  const managerOptions = (allUsers || []).filter((x) => x.id !== id && (MANAGER_ROLES[form?.role] || []).includes(x.role));
  const reportsToHint = form?.role === "cleaner" ? t("sp.hintCleaner", "Cleaners can report to a Supervisor, Admin or Owner.")
    : form?.role === "supervisor" ? t("sp.hintSup", "Supervisors report to an Admin or Owner.")
    : form?.role === "office" ? t("sp.hintOffice", "Office staff can report to a Supervisor, Admin or Owner.")
    : t("sp.hintOther", "Select this person's manager.");

  const saveSites = async () => { try { const { data } = await client.put(`/users/${id}/sites`, siteSel.map((s) => ({ siteId: s }))); if (data?.warning) toast.warning(data.warning); else toast.success(t("common.saved", "Saved")); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const saveSupervisor = async () => {
    try {
      await client.put(`/users/${id}/supervisor`, { staffUserId: supervisorId || null, isPrimary: true });
      toast.success(t("common.saved", "Saved"));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const setStatus = async (action) => {
    if (!(await confirm({ title: t(`staff.${action}`, action), description: t(`staff.${action}Confirm`, `${action} this user?`), confirmText: t(`staff.${action}`, action), destructive: action !== "reactivate" }))) return;
    try { await client.post(`/users/${id}/${action}`); toast.success(t("common.saved", "Saved")); loadUser(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const del = async () => {
    if (!(await confirm({ title: t("staff.delete", "Delete user"), description: t("staff.deleteConfirm", "Permanently delete this user? Their owned records are removed; records shared with genuine staff are preserved. This cannot be undone."), confirmText: t("common.delete", "Delete"), destructive: true }))) return;
    setBusy(true);
    try { await client.delete(`/users/${id}`); toast.success(t("staff.deleted", "User deleted")); nav("/staff"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    try { await client.post(`/users/${id}/private-notes`, { body: noteBody }); setNoteBody(""); const { data } = await client.get(`/users/${id}/private-notes`); setNotes(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delNote = async (nid) => { try { await client.delete(`/users/${id}/private-notes/${nid}`); setNotes((n) => n.filter((x) => x.id !== nid)); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  if (!u) return <div className="p-8 text-slate-400" data-testid="staff-profile-loading">Loading…</div>;
  const inactive = u.status === "deactivated";

  return (
    <div className="space-y-6" data-testid="staff-profile-page">
      <button onClick={() => nav("/staff")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand" data-testid="staff-back">
        <ArrowLeft className="h-4 w-4" />{t("nav.staff", "Staff & Access")}
      </button>

      <Card className="p-6 border-slate-200">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-brand/10 text-brand flex items-center justify-center text-lg font-bold">{(u.displayName || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}</div>
            <div>
              <h2 className="font-display text-2xl font-bold text-brand-ink flex items-center gap-2">{u.displayName}{inactive && <Badge variant="outline" className="bg-slate-100 text-slate-500">{t("staff.inactive", "Inactive")}</Badge>}</h2>
              <p className="text-sm text-slate-500 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{u.email}</p>
              <div className="flex gap-2 mt-2">
                <Badge variant="outline" className={ROLE_BADGE[u.role] || ""}>{t(`role.${u.role}`, u.role)}</Badge>
                <Badge variant="outline" className="bg-slate-50">{t(`wt.${u.workerType}`, u.workerType)}</Badge>
                <Badge variant="outline" className="bg-slate-50"><Globe className="h-3 w-3 mr-1" />{(u.languagePref || "en").toUpperCase()}</Badge>
                <Badge variant="outline" className={OA_STYLE[u.operationalAccess] || OA_STYLE.CONNECT_ONLY} data-testid="sp-oa-badge"><Layers className="h-3 w-3 mr-1" />{OA_LABEL[u.operationalAccess] || "Connect only"}</Badge>
              </div>
            </div>
          </div>
          {can("message.send") && messagingEnabled && (
            <Button size="sm" variant="outline" data-testid="staff-message" className="mr-2"
              onClick={() => { window.location.href = `/messages?dm=${u.id}&name=${encodeURIComponent(u.displayName)}`; }}>
              <Mail className="h-4 w-4 mr-1.5" />{t("staff.message", "Message")}
            </Button>
          )}
          {can("user.manage") && (
            <div className="flex gap-2" data-testid="staff-status-actions">
              {inactive
                ? <Button size="sm" variant="outline" data-testid="staff-reactivate" onClick={() => setStatus("reactivate")}><UserCheck className="h-4 w-4 mr-1.5" />{t("staff.reactivate", "Reactivate")}</Button>
                : <Button size="sm" variant="outline" className="text-amber-600 border-amber-200" data-testid="staff-deactivate" onClick={() => setStatus("deactivate")}><UserX className="h-4 w-4 mr-1.5" />{t("staff.deactivate", "Deactivate")}</Button>}
              <Button size="sm" variant="outline" className="text-red-600 border-red-200" data-testid="staff-delete" onClick={del} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}{t("common.delete", "Delete")}</Button>
            </div>
          )}
        </div>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="sp-tab-overview"><User className="h-4 w-4 mr-1.5" />{t("sp.overview", "Overview")}</TabsTrigger>
          {can("permission.manage") && <TabsTrigger value="access" data-testid="sp-tab-access"><Shield className="h-4 w-4 mr-1.5" />{t("sp.access", "Access")}</TabsTrigger>}
          {can("user.manage") && <TabsTrigger value="notes" data-testid="sp-tab-notes"><Lock className="h-4 w-4 mr-1.5" />{t("staff.tabNotes", "Private Notes")}</TabsTrigger>}
          {can("user.view") && <TabsTrigger value="activity" data-testid="sp-tab-activity"><Activity className="h-4 w-4 mr-1.5" />{t("sp.activity", "Activity")}</TabsTrigger>}
          {isOA && <TabsTrigger value="holiday" data-testid="sp-tab-holiday"><Palmtree className="h-4 w-4 mr-1.5" />{t("sp.holiday", "Holiday pay")}</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="pt-4">
          {can("user.manage") && form ? (
            <Card className="p-5 border-slate-200 max-w-2xl" data-testid="sp-edit-card">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-1.5"><User className="h-4 w-4" />{t("sp.editDetails", "Edit details")}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("common.name", "Name")}</Label>
                  <Input data-testid="sp-edit-name" className="mt-1" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
                  <Label className="text-xs mt-2 block">{t("sp.phone", "Phone")}</Label>
                  <Input data-testid="sp-edit-phone" className="mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+44…" />
                </div>
                <div>
                  <Label className="text-xs">{t("common.email", "Email")}</Label>
                  <Input data-testid="sp-edit-email" type="email" className="mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">{t("common.role", "Role")}</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="sp-edit-role" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.roles || ["owner", "admin", "supervisor", "office", "cleaner"]).filter((r) => r !== "owner" || can("user.manage")).map((r) => <SelectItem key={r} value={r} className="capitalize">{t(`role.${r}`, r)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("staff.lblWorkerType", "Worker type")}</Label>
                  <Select value={form.workerType} onValueChange={(v) => setForm({ ...form, workerType: v })}>
                    <SelectTrigger data-testid="sp-edit-workertype" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.workerTypes || ["static", "field", "hybrid"]).map((w) => <SelectItem key={w} value={w} className="capitalize">{t(`wt.${w}`, w)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("staff.lblLanguage", "Language")}</Label>
                  <Select value={form.languagePref} onValueChange={(v) => setForm({ ...form, languagePref: v })}>
                    <SelectTrigger data-testid="sp-edit-language" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.languages || ["en", "pl", "uk", "ro"]).map((l) => <SelectItem key={l} value={l} className="uppercase">{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("common.status", "Status")}</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="sp-edit-status" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{t("st.active", "Active")}</SelectItem>
                      <SelectItem value="deactivated">{t("staff.inactive", "Inactive")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">{t("sp.reportsTo", "Reports to")}</Label>
                  <Select value={form.reportsTo || "none"} onValueChange={(v) => setForm({ ...form, reportsTo: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="sp-edit-reportsto" className="mt-1"><SelectValue placeholder={t("sp.reportsToPh", "Select manager")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("sp.reportsToNone", "— None —")}</SelectItem>
                      {managerOptions.map((m) => <SelectItem key={m.id} value={m.id}>{m.displayName} · {t(`role.${m.role}`, m.role)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-400 mt-1">{reportsToHint}</p>
                  <div className="mt-2 p-2.5 rounded-lg bg-slate-50 border border-slate-200 max-w-md" data-testid="sp-reportsto-explain">
                    <p className="text-[11px] text-slate-600"><span className="font-semibold text-slate-700">{t("sp.reportsToWhat", "What this is:")}</span> {t("sp.reportsToExplain", "Sets the organisation hierarchy — it powers the Org Chart and team-scoped views (Attendance, the dashboard \"Live now\" figures and reports). It does not grant any access on its own.")}</p>
                    <p className="text-[11px] text-slate-500 mt-1">{t("sp.reportsToVsSup", "Different from the \"Supervisor\" link on the Access tab, which is the single primary manager used for approvals and oversight.")}</p>
                  </div>
                </div>
              </div>
              <Button size="sm" className="mt-4 bg-brand hover:bg-brand-dark" data-testid="sp-save-profile" onClick={saveProfile} disabled={savingProfile || !form.displayName || !form.email}>
                {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save", "Save changes")}
              </Button>
            </Card>
          ) : (
            <Card className="p-5 border-slate-200 space-y-2 max-w-xl">
              <Row icon={User} label={t("common.name", "Name")} value={u.displayName} />
              <Row icon={User} label={t("sp.phone", "Phone")} value={u.phone || "—"} />
              <Row icon={Mail} label={t("common.email", "Email")} value={u.email} />
              <Row icon={Shield} label={t("common.role", "Role")} value={t(`role.${u.role}`, u.role)} />
              <Row icon={User} label={t("staff.lblWorkerType", "Worker type")} value={t(`wt.${u.workerType}`, u.workerType)} />
              <Row icon={Globe} label={t("staff.thLang", "Language")} value={(u.languagePref || "en").toUpperCase()} />
              <Row icon={Building2} label={t("common.status", "Status")} value={inactive ? t("staff.inactive", "Inactive") : t("st.active", "Active")} />
              <Row icon={Users} label={t("sp.reportsTo", "Reports to")} value={(allUsers.find((x) => x.id === u.reportsTo)?.displayName) || "—"} />
            </Card>
          )}

          <Card className="p-5 border-slate-200 max-w-2xl mt-4" data-testid="sp-operational-access">
            <h3 className="font-semibold text-slate-800 mb-1 flex items-center gap-1.5"><Layers className="h-4 w-4" />{t("sp.opAccess", "Operational Access")}</h3>
            <p className="text-xs text-slate-500 mb-3">{t("sp.opAccessDesc", "Controls which product experience this worker receives on mobile. This is an operational capability — it does not grant any management permission.")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
              <div className="rounded-lg border border-sky-100 bg-sky-50/50 p-3"><p className="text-[11px] font-bold uppercase tracking-wide text-sky-700 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />iClean Connect</p><p className="text-xs text-slate-600 mt-1">{t("sp.connectDesc", "Static sites, shifts and clock in / out.")}</p></div>
              <div className="rounded-lg border border-violet-100 bg-violet-50/50 p-3"><p className="text-[11px] font-bold uppercase tracking-wide text-violet-700 flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" />iClean Field</p><p className="text-xs text-slate-600 mt-1">{t("sp.fieldDesc", "Field jobs and advanced field operations.")}</p></div>
            </div>
            {can("user.manage") ? (
              <>
                <RadioGroup value={oaValue} onValueChange={setOaValue} className="space-y-2" data-testid="sp-oa-radiogroup">
                  {OA_OPTIONS.map((o) => (
                    <label key={o.value} data-testid={`sp-oa-opt-${o.value}`} className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${oaValue === o.value ? "border-brand bg-brand/5" : "border-slate-200 hover:bg-slate-50"}`}>
                      <RadioGroupItem value={o.value} className="mt-0.5" />
                      <span><span className="text-sm font-medium text-slate-800">{o.label}</span><span className="block text-xs text-slate-500">{o.desc}</span></span>
                    </label>
                  ))}
                </RadioGroup>
                {u.needsAccessReview && <p className="text-xs text-amber-600 mt-3 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" />{t("sp.oaReview", "This access was set automatically during migration — please confirm it is correct.")}</p>}
                <Button size="sm" className="mt-4 bg-brand hover:bg-brand-dark" data-testid="sp-oa-save" onClick={saveOperationalAccess} disabled={savingOA || oaValue === (u.operationalAccess || "CONNECT_ONLY")}>
                  {savingOA ? <Loader2 className="h-4 w-4 animate-spin" /> : t("sp.oaSave", "Save operational access")}
                </Button>
              </>
            ) : (
              <Badge variant="outline" className={OA_STYLE[u.operationalAccess] || OA_STYLE.CONNECT_ONLY}>{OA_LABEL[u.operationalAccess] || "Connect only"}</Badge>
            )}
          </Card>
        </TabsContent>

        {can("permission.manage") && (
          <TabsContent value="access" className="pt-4 space-y-4">
            <StaffPasswordCard userId={id} />
            <Card className="p-5 border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><Building2 className="h-4 w-4" />{t("staff.tabSites", "Assigned Sites")}</h3>
              <p className="text-xs text-slate-500 mb-2">{t("sp.sitesDesc", "Which sites this staff member is assigned to.")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {sites.map((s) => (
                  <label key={s.id} data-testid={`sp-site-${s.id}`} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
                    <Checkbox checked={siteSel.includes(s.id)} onCheckedChange={(c) => setSiteSel((p) => c ? [...p, s.id] : p.filter((x) => x !== s.id))} />
                    <span className="text-sm text-slate-700">{s.name}</span>
                  </label>
                ))}
              </div>
              <Button size="sm" className="mt-3 bg-brand hover:bg-brand-dark" data-testid="sp-save-sites" onClick={saveSites}>{t("common.save", "Save sites")}</Button>
            </Card>
            <Card className="p-5 border-slate-200" data-testid="sp-supervisor-card">
              <h3 className="font-semibold text-slate-800 flex items-center gap-1.5"><Users className="h-4 w-4" />{t("sp.supervisorTitle", "Supervisor")}</h3>
              <p className="text-sm text-slate-500 mt-0.5">{t("sp.supervisorLead", "Primary manager for day-to-day approvals & oversight.")}</p>
              <p className="text-xs text-slate-500 mt-2 max-w-xl">{t("sp.supervisorDesc", "The single primary supervisor responsible for this person's timesheet approvals, management and team oversight. This is separate from \"Reports to\" on the Profile tab, which sets the organisation hierarchy for the Org Chart and team-scoped views. Assigning a supervisor here does not grant access to this staff member's sites or payroll.")}</p>

              <div className="mt-3 max-w-sm">
                <Select value={supervisorId || "none"} onValueChange={(v) => setSupervisorId(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="sp-supervisor-select" className="h-auto py-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none"><span className="text-slate-500">{t("sp.noSupervisor", "No supervisor assigned")}</span></SelectItem>
                    {supervisors.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <span className="flex flex-col leading-tight py-0.5">
                          <span className="font-medium text-slate-800">{s.displayName}</span>
                          <span className="text-[11px] text-slate-500">{t(`role.${s.role}`, roleLabel(s.role))}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" className="mt-3 bg-brand hover:bg-brand-dark" data-testid="sp-save-supervisor" onClick={saveSupervisor}>{t("common.save", "Save")}</Button>
              </div>

              {supervisorId && (() => {
                const sup = supervisors.find((s) => s.id === supervisorId);
                if (!sup) return null;
                return (
                  <div className="mt-4 p-3 rounded-lg border border-slate-200 bg-slate-50/60 max-w-sm" data-testid="sp-supervisor-summary">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-brand">{t("sp.primarySupervisor", "Primary Supervisor")}</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">{sup.displayName}</p>
                    <p className="text-xs text-slate-500">{t(`role.${sup.role}`, roleLabel(sup.role))}</p>
                    <p className="text-[10px] uppercase tracking-wide text-slate-400 mt-2">{t("sp.assignedResp", "Assigned responsibility")}</p>
                    <p className="text-xs text-slate-600">{t("sp.respValue", "Staff management and approved operational oversight")}</p>
                  </div>
                );
              })()}

              <div className="mt-4 p-3 rounded-lg bg-blue-50/60 border border-blue-100 max-w-xl" data-testid="sp-supervisor-note">
                <p className="text-xs font-semibold text-blue-800 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" />{t("sp.noGrantTitle", "Supervisor assignment does not grant permissions.")}</p>
                <p className="text-xs text-blue-700/80 mt-1">{t("sp.noGrantBody", "Site access is controlled separately under Sites, and payroll access is controlled separately by role and permission.")}</p>
              </div>
            </Card>
          </TabsContent>
        )}

        {can("user.manage") && (
          <TabsContent value="notes" className="pt-4" data-testid="sp-private-notes">
            <Card className="p-5 border-slate-200 space-y-3">
              <p className="text-xs text-slate-500 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-amber-500" />{t("staff.notesHint", "Management-only. Never visible to the worker or in the mobile app. All changes are audited.")}</p>
              <div className="flex gap-2">
                <Input data-testid="sp-note-input" value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder={t("staff.notePlaceholder", "Add a private management note…")} />
                <Button data-testid="sp-note-add" onClick={addNote} disabled={!noteBody.trim()} className="bg-brand hover:bg-brand-dark">{t("common.add", "Add")}</Button>
              </div>
              <div className="space-y-2">
                {notes.length === 0 ? <p className="text-sm text-slate-400">{t("staff.noNotes", "No private notes yet.")}</p> :
                  notes.map((n) => (
                    <div key={n.id} data-testid={`sp-note-${n.id}`} className="flex items-start justify-between gap-2 px-3 py-2 rounded-lg border border-amber-100 bg-amber-50/40">
                      <div><p className="text-sm text-slate-700 whitespace-pre-wrap flex gap-1.5"><StickyNote className="h-3.5 w-3.5 mt-0.5 text-amber-500 shrink-0" />{n.body}</p><p className="text-[10px] text-slate-400 mt-1">{n.authorName} · {new Date(n.createdAt).toLocaleString()}</p></div>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 shrink-0" data-testid={`sp-note-del-${n.id}`} onClick={() => delNote(n.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
              </div>
            </Card>
          </TabsContent>
        )}

        {can("user.view") && (
          <TabsContent value="activity" className="pt-4">
            <Card className="p-5 border-slate-200">
              <p className="text-sm text-slate-500 mb-3">{t("sp.jobCount", "Assigned jobs")}: <b className="text-slate-800">{activity.jobCount}</b></p>
              <div className="space-y-1.5">
                {(activity.items || []).length === 0 ? <p className="text-sm text-slate-400">{t("sp.noActivity", "No recent activity.")}</p> :
                  activity.items.map((it, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-slate-50 py-1.5">
                      <span className="text-slate-700 capitalize flex items-center gap-1.5"><Activity className="h-3.5 w-3.5 text-slate-400" />{String(it.type || "").replace(/_/g, " ")}</span>
                      <span className="text-xs text-slate-400">{it.at ? new Date(it.at).toLocaleString() : ""}</span>
                    </div>
                  ))}
              </div>
            </Card>
          </TabsContent>
        )}
        {isOA && <TabsContent value="holiday" className="pt-4"><HolidayPay userId={id} /></TabsContent>}
      </Tabs>
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 py-1.5 border-b border-slate-50 last:border-0">
    <Icon className="h-4 w-4 text-slate-400" /><span className="text-xs uppercase tracking-wide text-slate-400 w-28">{label}</span><span className="text-sm text-slate-700">{value}</span>
  </div>
);
